import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";

// ─────────────────────────────────────────────────────────────
// PATH NOTE
// This file assumes it lives at: src/SUPERAPP/SHOPPING/FurnitureHome.tsx
// — the same folder as BabyHome.tsx, so every relative import below is
// identical to that file's. Wire it in AtlasHome exactly like Baby:
//   import FurnitureHome from "../SHOPPING/FurnitureHome";
// …against whatever subcategory id your Furniture tile uses.
// If you move it deeper (e.g. SHOPPING/FURNITURE/FurnitureHome.tsx)
// add one more "../" to every import.
//
// No bottom tab bar here on purpose: AtlasHome owns the mobile tabbar
// and the desktop sidebar, this page is rendered inside that shell.
// ─────────────────────────────────────────────────────────────
import type { AppDispatch, RootState } from "../../../../redux/store";

import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
  setFilters,
} from "../../../../redux/storeProductSlice";
import { addToCart, decrementCartItem, clearCart } from "../../../../redux/storeCartSlice";
import { fetchAllCommercePartners } from "../../../../redux/commercePartnersSlice";
import { fetchAllProductSummaries } from "../../../../redux/storeProductRatingSlice";
import { logout } from "../../../../redux/authSlice";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../../../redux/notificationsSlice";
import { CurrencyContext } from "../../../../GLOBAL/CurrencyContext";
import appLogo from "../../../../assets/app/logo.png";

/* ═══════════════════════════════════════════════════════════
   ⭐ CATEGORY SOURCE — the SAME collection StoreProducts saves
   with. Products store `categoryId` + `attributes`, so this page
   filters/labels by walking the same tree back up. This page is
   the Furniture department (10) and nothing else.
═══════════════════════════════════════════════════════════ */
import {
  AllCategoriesFlat,
  type CategoryItem,
} from "../../../../CATEGORIES/CategoriesCollection";
import { attributesForCategory } from "../../../../CATEGORIES/ATTRIBUTES/CategoriesCollectionAttributes";

import {
  Search, ShoppingBag, X, ChevronRight, ChevronDown, Plus, Minus,
  Heart, User, ShoppingCart, MapPin, Globe, LogOut, AlertTriangle,
  Package, Star, LayoutGrid, Check, Tag, Ruler, Layers, Sofa, Armchair,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   SCOPE
═══════════════════════════════════════════════════════════ */
const FURNITURE_DEPT_ID = "09";

/** Studio photos of furniture are almost always shot on white.
 *  Multiply-blending them onto the plinth removes that white box.
 *  Flip to false if your sellers upload dark-background shots. */
const PHOTO_BLEND = true;

/* ═══════════════════════════════════════════════════════════
   HEARTLAS DESIGN TOKENS — identical to StoreProductsHome
═══════════════════════════════════════════════════════════ */
const HEART = {
  bg: "#FBF6EE",
  panelBg: "#FFFFFF",

  dark: "#1B0E06",
  black: "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",

  red: "#C8202E",
  redDark: "#9E1521",
  redSoft: "#FBE7E9",

  gold: "#F2A93E",
  goldDark: "#D4900F",
  goldSoft: "#FCEFD4",

  border: "#EEDFC4",
  divider: "#F3E6CE",
};

/* the showroom floor — the only tokens this page adds */
const FLOOR = {
  wall: "#FFFDF8",
  wallEdge: "#FBF3E4",
  boardLight: "#F3E3C7",
  boardDark: "#E9D5B2",
};

const T = {
  pageBg: HEART.bg,
  cardBg: HEART.panelBg,
  black: HEART.dark,
  muted: HEART.textMuted,
  mutedDim: HEART.textFaint,
  red: HEART.red,
  redDim: HEART.redSoft,
  border: HEART.border,
  grad: `linear-gradient(135deg, ${HEART.red} 0%, ${HEART.redDark} 100%)`,
  gradH: `linear-gradient(90deg, ${HEART.red} 0%, ${HEART.redDark} 100%)`,
  outline: `1.5px solid ${HEART.border}`,
};

const GRAD_H = T.gradH;
const RGBA_RED = (a: number) => `rgba(200,32,46,${a})`;

/* accent comes straight from the product's brand — no random tints */
const brandAccentColor = (partner: any): string =>
  partner?.brandColor || partner?.themeColor || partner?.color || HEART.red;

/* ═══════════════════════════════════════════════════════════
   CATEGORY TREE HELPERS — ancestry always resolved by walking
   parentId, never by string prefixes (PT-… / PRD-… break those).
═══════════════════════════════════════════════════════════ */
const CAT_MAP = new Map<string, CategoryItem>();
AllCategoriesFlat.forEach((c) => CAT_MAP.set(c.id, c));

const childrenOf = (parentId: string | null) =>
  AllCategoriesFlat.filter((c) => c.parentId === parentId);

const chainCache = new Map<string, string[]>();
const chainIdsOf = (id?: string | number | null): string[] => {
  if (id === null || id === undefined || id === "") return [];
  const key = String(id);
  const hit = chainCache.get(key);
  if (hit) return hit;
  const chain: string[] = [];
  let cur = CAT_MAP.get(key);
  while (cur) {
    chain.unshift(cur.id);
    cur = cur.parentId ? CAT_MAP.get(cur.parentId) : undefined;
  }
  chainCache.set(key, chain);
  return chain;
};

const catName = (id?: string | number | null) =>
  id === null || id === undefined ? "" : CAT_MAP.get(String(id))?.name ?? "";

/** Full path, department stripped — everything here is Furniture. */
const catPathLabel = (id?: string | number | null) =>
  chainIdsOf(id).filter((cid) => cid !== FURNITURE_DEPT_ID).map(catName).join(" › ");

/** Furniture's own categories — the rooms, on the oval rail. */
const FURNITURE_CATEGORIES: CategoryItem[] = childrenOf(FURNITURE_DEPT_ID);

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTES — same bridge StoreProducts / PSH use, plus a
   walk-up fallback so deep nodes still resolve.
═══════════════════════════════════════════════════════════ */
const toAttributeCategoryId = (id?: string | number | null): string => {
  if (id === null || id === undefined || id === "") return "";
  const chain = chainIdsOf(id).map((cid) => CAT_MAP.get(cid)!).filter(Boolean);
  const anchor = [...chain].reverse().find((c) => c.level === "category" || c.level === "department");
  return anchor ? `0${anchor.id}` : "";
};

const attrCache = new Map<string, any[]>();
const resolveAttributes = (categoryId?: string | number | null): any[] => {
  if (categoryId === null || categoryId === undefined || categoryId === "") return [];
  const key = String(categoryId);
  const hit = attrCache.get(key);
  if (hit) return hit;

  const chain = [...chainIdsOf(categoryId)].reverse();
  const keys = [toAttributeCategoryId(categoryId), ...chain.flatMap((c) => [`0${c}`, c])].filter(Boolean);

  let found: any[] = [];
  for (const k of keys) {
    try {
      const defs = attributesForCategory(k as any);
      if (defs && defs.length) { found = defs; break; }
    } catch { /* unknown key — keep walking up */ }
  }
  attrCache.set(key, found);
  return found;
};

const resolveAttrValue = (attr: any, value: any): string => {
  if (value === undefined || value === null || value === "") return "";
  const label = (optId: any) => {
    const opt = attr.options?.find((o: any) => String(o.id) === String(optId));
    return opt ? (opt.name || opt.standard || String(optId)) : String(optId);
  };
  if (Array.isArray(value)) return value.map(label).join(", ");
  if (attr.options?.length) return label(value);
  return `${value}${attr.unit ? ` ${attr.unit}` : ""}`;
};

const isEmptyValue = (v: any) =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

/** Attributes whose options make them a buyable variant (Finish, Fabric…). */
const isVariantAttr = (attr: any) =>
  (attr?.inputType === "select" || !attr?.inputType) &&
  Array.isArray(attr?.options) && attr.options.length > 0;

const variantAttrsForItem = (item: any) => resolveAttributes(item?.categoryId).filter(isVariantAttr);
const hasVariants = (item: any) => variantAttrsForItem(item).length > 0;

interface Detail { name: string; value: string }
/** Saved, non-variant attributes → the spec sheet. */
const detailsOf = (item: any): Detail[] => {
  const saved = item?.attributes ?? {};
  return resolveAttributes(item?.categoryId)
    .filter((def: any) => !isVariantAttr(def))
    .filter((def: any) => !isEmptyValue(saved[String(def.id)]))
    .map((def: any) => ({ name: def.name, value: resolveAttrValue(def, saved[String(def.id)]) }))
    .filter((d) => d.value);
};

/* Furniture is bought on two questions before any other: will it fit,
   and what is it made of. Both get pulled onto the card. */
const SIZE_RE = /dimension|width|depth|height|length|footprint|seat|size/i;
const MATERIAL_RE = /material|fabric|finish|wood|upholster|leather|frame/i;

const firstDetailMatching = (item: any, re: RegExp): Detail | null => {
  const saved = item?.attributes ?? {};
  const def = resolveAttributes(item?.categoryId)
    .filter((d: any) => !isVariantAttr(d))
    .find((d: any) => re.test(d.name || "") && !isEmptyValue(saved[String(d.id)]));
  if (!def) return null;
  const value = resolveAttrValue(def, saved[String(def.id)]);
  return value ? { name: def.name, value } : null;
};

const sizeDetailOf = (item: any) => firstDetailMatching(item, SIZE_RE);
const materialDetailOf = (item: any) => firstDetailMatching(item, MATERIAL_RE);

type SortKey = "default" | "price_asc" | "price_desc" | "newest" | "name_asc";

const isOutOfStock = (item: any) => item?.available === false || (item?.stock ?? 0) <= 0;

/* ═══════════════════════════════════════════════════════════
   LOCATION TIERS — same as PSH
   0 = same city, 1 = same country, 2 = same continent, 3 = world
═══════════════════════════════════════════════════════════ */
const COUNTRY_TO_CONTINENT: Record<string, string> = {
  estonia: "Europe", latvia: "Europe", lithuania: "Europe", finland: "Europe",
  sweden: "Europe", norway: "Europe", denmark: "Europe", germany: "Europe",
  france: "Europe", spain: "Europe", italy: "Europe", portugal: "Europe",
  poland: "Europe", "united kingdom": "Europe", ireland: "Europe",
  netherlands: "Europe", belgium: "Europe", austria: "Europe",
  switzerland: "Europe", greece: "Europe",
  "united states": "North America", canada: "North America", mexico: "North America",
  brazil: "South America", argentina: "South America",
  nigeria: "Africa", kenya: "Africa", "south africa": "Africa",
  china: "Asia", japan: "Asia", india: "Asia", "united arab emirates": "Asia",
  australia: "Oceania", "new zealand": "Oceania",
};
const getContinent = (country?: string | null): string | null =>
  country ? COUNTRY_TO_CONTINENT[country.toLowerCase().trim()] ?? null : null;
const isLocalTier = (tier: number) => tier <= 1;
const isGlobalTier = (tier: number) => tier >= 2;

/** A sofa's shipping distance is part of its price. Say it out loud. */
const TIER_LABEL = ["In your city", "In your country", "Ships from your region", "Ships worldwide"];

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS — PSH's fonts, PSH's oval, plus the one thing
   that makes this page Furniture: the plinth.
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600;700;800;900&family=Archivo+Black&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
.fn-page ::-webkit-scrollbar { display: none; }
.fn-page input, .fn-page button, .fn-page select { font-family: inherit; }
.fn-page input::placeholder { color: rgba(255,255,255,.6); }
.fn-page :focus-visible { outline: 2px solid ${HEART.gold}; outline-offset: 2px; }

@keyframes gradSpin { 0%{background-position:0% 50%;} 50%{background-position:100% 50%;} 100%{background-position:0% 50%;} }
@keyframes titleGlow { 0%,100%{ text-shadow: 0 2px 0 rgba(27,14,6,.08), 0 14px 30px rgba(200,32,46,.18);} 50%{ text-shadow: 0 2px 0 rgba(27,14,6,.08), 0 18px 40px rgba(200,32,46,.30);} }
@keyframes fnPulse { 0%,100%{opacity:1} 50%{opacity:.35} }
@keyframes ovalSheen { 0% { transform: translateX(-160%) skewX(-18deg); } 60%,100% { transform: translateX(320%) skewX(-18deg); } }
@keyframes cardPop { from{opacity:0;transform:translateY(10px) scale(.97);} to{opacity:1;transform:none;} }
@keyframes fnFloat { 0%,100%{transform:translateY(0);} 50%{transform:translateY(-5px);} }

.fn-title {
  font-family: 'Archivo Black','Montserrat',sans-serif;
  font-weight: 900; text-transform: uppercase; letter-spacing: -0.02em; line-height: .95;
  background: linear-gradient(180deg, ${HEART.dark} 0%, ${HEART.dark} 60%, ${HEART.red} 130%);
  -webkit-background-clip: text; background-clip: text; color: transparent;
  animation: titleGlow 3.4s ease-in-out infinite;
}

.fn-hbtn { display:flex; align-items:center; gap:6px; background:rgba(255,255,255,.16);
  border:1px solid rgba(255,255,255,.3); cursor:pointer; color:#fff;
  font-size:12px; font-weight:700; padding:9px 14px; border-radius:999px;
  flex-shrink:0; white-space:nowrap; transition:background .2s; }
.fn-hbtn:hover { background:rgba(255,255,255,.26); }

/* ══════════════════════════════════════════════
   THE OVAL — same glassy capsule as PSH, holding
   the rooms instead of departments.
══════════════════════════════════════════════ */
.fn-oval {
  position: relative; display: flex; align-items: center; gap: 4px;
  padding: 7px 10px; border-radius: 999px;
  background:
    linear-gradient(#FFFDF8, #FFF6E6) padding-box,
    linear-gradient(120deg, ${HEART.gold}, ${HEART.red} 45%, ${HEART.gold} 90%) border-box;
  border: 2px solid transparent;
  box-shadow: 0 10px 30px rgba(200,32,46,.14), 0 2px 6px rgba(27,14,6,.08), inset 0 1px 0 rgba(255,255,255,.9);
  overflow-x: auto; overflow-y: hidden; scrollbar-width: none; -webkit-overflow-scrolling: touch;
}
.fn-oval::after {
  content: ""; position: absolute; top: 0; bottom: 0; left: 0; width: 90px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.75), transparent);
  animation: ovalSheen 5.5s ease-in-out infinite; pointer-events: none;
}
.fn-oval-toggle {
  position: relative; flex-shrink: 0; display: flex; align-items: center; gap: 7px;
  border: none; cursor: pointer; border-radius: 999px; padding: 9px 16px;
  font-size: 12px; font-weight: 800; letter-spacing: .03em;
  color: ${HEART.textMuted}; background: transparent; white-space: nowrap;
  transition: color .18s, transform .18s;
}
.fn-oval-toggle:hover { color: ${HEART.dark}; transform: translateY(-1px); }
.fn-oval-toggle .fn-oval-ic { display:flex; font-size:13px; transition: transform .18s; }
.fn-oval-toggle:hover .fn-oval-ic { transform: rotate(-8deg) scale(1.12); }
.fn-oval-toggle.active {
  color:#fff; background: linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});
  box-shadow: 0 8px 18px rgba(200,32,46,.42), inset 0 1px 0 rgba(255,255,255,.28);
}
.fn-oval-toggle.active .fn-oval-ic { transform: none; }

/* ══════════════════════════════════════════════
   THE PLINTH — the only thing that isn't PSH.
   Furniture is sold standing in a room, so every
   piece gets a showroom: a wall, a floor line, a
   contact shadow. The photo sits ON something.
══════════════════════════════════════════════ */
.fn-card { background:${HEART.panelBg}; border:1.5px solid ${HEART.border}; border-radius:18px;
  padding:10px 10px 14px; cursor:pointer; display:flex; flex-direction:column; position:relative;
  animation: cardPop .3s ease both; transition: box-shadow .22s, transform .22s, border-color .22s; }
.fn-card:hover { transform: translateY(-6px); box-shadow: 0 4px 10px rgba(27,14,6,.06), 0 24px 48px rgba(27,14,6,.16); }

.fn-plinth { position:relative; width:100%; aspect-ratio:4/3; overflow:hidden; border-radius:14px;
  background:
    linear-gradient(180deg, ${FLOOR.wall} 0%, ${FLOOR.wallEdge} 68%, ${FLOOR.boardLight} 68%, ${FLOOR.boardDark} 100%);
  display:flex; align-items:center; justify-content:center; }
/* the floor line — a hairline where wall meets boards */
.fn-plinth::before {
  content:""; position:absolute; left:0; right:0; top:68%; height:1px;
  background:rgba(27,14,6,.10); pointer-events:none; }
/* the contact shadow — what makes the piece stand rather than float */
.fn-shadow {
  position:absolute; left:16%; right:16%; top:70%; height:11%; border-radius:50%;
  background:radial-gradient(closest-side, rgba(27,14,6,.26), rgba(27,14,6,0) 78%);
  filter:blur(1px); pointer-events:none; }
.fn-plinth img {
  position:relative; z-index:1; width:100%; height:100%; object-fit:contain;
  padding:7% 9% 11%; display:block; transition: transform .35s ease; }
.fn-plinth.blend img { mix-blend-mode:multiply; }
.fn-card:hover .fn-plinth img { transform: scale(1.04) translateY(-2px); }

.fn-chip { display:inline-flex; align-items:center; gap:4px; font-size:10px; font-weight:700;
  color:${HEART.textMuted}; background:${HEART.bg}; border:1px solid ${HEART.border};
  border-radius:999px; padding:3px 9px; white-space:nowrap; }
.fn-chip.size { color:${HEART.goldDark}; background:${HEART.goldSoft}; border-color:${HEART.gold}; }

/* spec sheet in the detail modal — measurements read better as rows */
.fn-spec { display:grid; grid-template-columns:auto 1fr; gap:0; border:1px solid ${HEART.border};
  border-radius:12px; overflow:hidden; }
.fn-spec dt { padding:8px 12px; font-size:11px; font-weight:800; color:${HEART.textMuted};
  background:${HEART.bg}; border-bottom:1px solid ${HEART.divider}; white-space:nowrap; }
.fn-spec dd { padding:8px 12px; font-size:11.5px; font-weight:600; color:${HEART.dark};
  border-bottom:1px solid ${HEART.divider}; }
.fn-spec dt:last-of-type, .fn-spec dd:last-of-type { border-bottom:none; }

.fn-plus { transition: transform .15s; }
.fn-plus:hover { transform: scale(1.08); }
.fn-plus:active { transform: scale(.92); }

.fn-nav-item { display:flex; align-items:center; gap:12px; padding:13px 18px; cursor:pointer;
  font-size:14px; font-weight:700; color:${HEART.dark}; background:none; width:100%;
  text-align:left; border:none; border-bottom:1px solid ${HEART.divider}; transition:background .15s; }
.fn-nav-item:hover { background:${HEART.goldSoft}; }

.fn-scg-tile { background:none; border:none; cursor:pointer; padding:0; border-radius:14px; overflow:hidden; }

@media (prefers-reduced-motion: reduce) {
  .fn-page * { animation: none !important; }
  .fn-card, .fn-plinth img { transition: none; }
}
`;

/* ═══════════════════════════════════════════════════════════
   STYLES — PSH's shapes
═══════════════════════════════════════════════════════════ */
const A: Record<string, React.CSSProperties> = {
  commerceCard: { borderRadius: 18, padding: 18, background: "#fff", marginBottom: 16, boxShadow: "0 2px 16px rgba(27,14,6,.06)", border: T.outline },
  commerceHeader: { fontSize: 16, fontWeight: 900, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", color: T.black },
  category: { padding: 10, borderRadius: 12, background: HEART.bg, marginTop: 8, border: `1px solid ${HEART.border}`, cursor: "pointer" },
  row: { display: "flex", alignItems: "center", gap: 10, fontWeight: 700, color: T.black },
  sub: { paddingLeft: 20, paddingTop: 8 },
  sidebar: { background: "rgba(255,255,255,.98)", borderRadius: 20, padding: 20, boxShadow: "0 4px 24px rgba(27,14,6,.08)", position: "sticky" as const, top: 116, maxHeight: "calc(100vh - 130px)", overflowY: "auto" as const, border: T.outline },
};

const S: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", background: T.pageBg, paddingBottom: 40, fontFamily: "'Montserrat',sans-serif", position: "relative" as const },
  content: { position: "relative" as const, zIndex: 1 },
  headerTop: { background: GRAD_H, backgroundSize: "200% 100%", position: "sticky", top: 0, zIndex: 200, boxShadow: `0 4px 24px ${RGBA_RED(0.25)}` },
  searchWrap: { display: "flex", alignItems: "center", flex: 1, background: "rgba(255,255,255,.18)", borderRadius: 999, overflow: "hidden", border: `1.5px solid rgba(255,255,255,.32)` },
  searchInput: { flex: 1, border: "none", outline: "none", padding: "10px 14px", fontSize: 13, color: "#fff", background: "transparent", fontFamily: "inherit" },
  searchBtn: { width: 42, minHeight: 40, background: "rgba(255,255,255,.16)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, borderRadius: "0 999px 999px 0" },
  desktopGrid: { display: "grid", gap: 24, padding: "24px 24px 0" },
  /* wider than Baby's rail — landscape photos need the room */
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 20 },
  mobileGrid: { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 },
};

/* ═══════════════════════════════════════════════════════════
   PAGE WATERMARK — same as PSH
═══════════════════════════════════════════════════════════ */
const PageWatermark: React.FC = () => (
  <div
    aria-hidden="true"
    style={{
      position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
      backgroundImage: `url(${appLogo})`, backgroundRepeat: "repeat",
      backgroundSize: "170px 170px", opacity: 0.045, filter: "grayscale(1)",
    }}
  />
);

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
const FurnitureStore: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);
  const cur = symbol ?? "€";
  const money = (n: number) => `${cur}${((n ?? 0) * (rate ?? 1)).toFixed(2)}`;

  const authUser = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const userId = authUser?.userId ?? "";

  const filteredListings = useSelector((s: RootState) => s.storeProducts.filteredProducts);
  const cartItems = useSelector((s: RootState) => (s as any).storeCart?.items ?? []);
  const ratingSummaries = useSelector((s: RootState) => (s as any).storeProductRatings?.summaries ?? {});
  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);

  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    (commercePartners || []).forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [commercePartners]);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [catOpen, setCatOpen] = useState(false);
  const [desktopCatOpen, setDesktopCatOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSort, setActiveSort] = useState<SortKey>("default");
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  /* the room is the master toggle — this page is one department */
  const [activeCat, setActiveCat] = useState<string>(FURNITURE_CATEGORIES[0]?.id ?? FURNITURE_DEPT_ID);
  const cat = useMemo(
    () => FURNITURE_CATEGORIES.find((c) => c.id === activeCat) ?? FURNITURE_CATEGORIES[0],
    [activeCat]
  );
  const [activeSub, setActiveSub] = useState<string | null>(null);
  const [activeDeep, setActiveDeep] = useState<string | null>(null);

  const [openCatPanel, setOpenCatPanel] = useState<string | null>(FURNITURE_CATEGORIES[0]?.id ?? null);
  const [openNodes, setOpenNodes] = useState<Set<string>>(new Set());
  const [shopCatExpanded, setShopCatExpanded] = useState(true);

  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [userCity, setUserCity] = useState<string | null>(null);
  const [userLat, setUserLat] = useState<number | null>(null);
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showGlobal, setShowGlobal] = useState(false);
  const locationActive = !locationDenied && userLat !== null;

  const [qty, setQty] = useState(1);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState<any | null>(null);
  const [selectedVariants, setSelectedVariants] = useState<Record<string, any>>({});
  const [wished, setWished] = useState<Set<string>>(new Set());

  const cartTotal = cartItems.reduce((s: number, i: any) => s + (i.qty ?? 1), 0);

  /* ── brand distance tiers ── */
  const brandTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCity = userCity?.toLowerCase().trim();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    (commercePartners || []).forEach((p: any) => {
      const brandCity = p.address?.city?.toLowerCase().trim();
      const brandCountry = p.address?.country?.toLowerCase().trim();
      const brandContinent = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCity && brandCity && brandCity === deviceCity) tier = 0;
      else if (deviceCountry && brandCountry && brandCountry === deviceCountry) tier = 1;
      else if (deviceContinent && brandContinent && brandContinent === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [commercePartners, userCity, userCountry]);

  /* ── effects ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  useEffect(() => { dispatch(listenToPublishedProducts() as any); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries() as any); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllCommercePartners() as any); }, [dispatch]);
  useEffect(() => { dispatch(filterLocalProductsByCountry(null) as any); }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId) as any);
    return () => { dispatch(stopUserNotificationsListener() as any); };
  }, [dispatch, userId]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true);
      dispatch(filterLocalProductsByCountry(null) as any);
      return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude); setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } },
              (results: any, status: string) => {
                if (status === "OK" && results?.[0]) {
                  const comps = results[0].address_components;
                  const get = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                  setUserCity(get("locality") || get("administrative_area_level_2"));
                  const country = get("country"); setUserCountry(country);
                  if (country) dispatch(filterLocalProductsByCountry(country) as any);
                }
              });
          }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          dispatch(filterLocalProductsByCountry(null) as any);
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName })
        .then((r) => { r.onchange = () => { if (r.state === "granted") detect(); }; })
        .catch(() => {});
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  }, [userCountry, showGlobal, dispatch]);

  /* ── actions ── */
  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    setActiveSub(null); setActiveDeep(null);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(next ? null : userCountry) as any);
  };

  const resetAllFilters = () => {
    setActiveSub(null); setActiveDeep(null); setActiveSort("default"); setSearchQuery("");
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  };

  const handleCatSelect = (id: string) => {
    setActiveCat(id); setActiveSub(null); setActiveDeep(null);
    setOpenCatPanel(id); setOpenNodes(new Set());
  };
  const handleSubSelect = (id: string) => { setActiveSub((p) => (p === id ? null : id)); setActiveDeep(null); };
  const handleDeepSelect = (id: string) => setActiveDeep((p) => (p === id ? null : id));

  const toggleNode = (id: string) =>
    setOpenNodes((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty ?? 0;

  const SINGLE_BRAND_CART_MODE = true;
  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!SINGLE_BRAND_CART_MODE || !cartItems.length) return;
    const currentBrandId = cartItems[0]?.brandId;
    if (currentBrandId && newBrandId && currentBrandId !== newBrandId) dispatch(clearCart());
  };

  const openProductModal = (item: any) => {
    setDetailItem(item); setQty(1); setSelectedVariants({}); setDetailOpen(true);
  };
  const closeProductModal = () => { setDetailOpen(false); setDetailItem(null); };

  const handleAddToCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isOutOfStock(item)) return;
    if (hasVariants(item)) { openProductModal(item); return; }
    ensureCartBrandCompat(item.brandId);
    dispatch(addToCart({
      listingId: item.listingId, name: item.name!, rate: item.rate!,
      photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId,
    } as any));
  };

  const handleDetailContinue = (toCheckout: boolean) => {
    if (!detailItem || isOutOfStock(detailItem)) return;
    const vDefs = variantAttrsForItem(detailItem);
    if (vDefs.some((a: any) => selectedVariants[String(a.id)] == null)) return;

    ensureCartBrandCompat(detailItem.brandId);
    dispatch(addToCart({
      listingId: detailItem.listingId, name: detailItem.name!,
      rate: detailItem.rate!, qty,
      photo: detailItem.photos?.[0],
      hostId: detailItem.hostId, brandId: detailItem.brandId,
      selectedVariants: { ...selectedVariants },
    } as any));
    closeProductModal(); setQty(1); setSelectedVariants({});
    if (toCheckout) goToCheckout();
  };

  const toggleWish = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setWished((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const goToCheckout = () => navigate(`/pay/${userId}`, { state: { items: cartItems } });
  const goToBrand = (id: string) => { if (id && id !== "unknown") navigate(`/brand/${id}`); };
  const handleLogout = async () => { await dispatch(logout() as any); navigate("/AuthSwitch"); };

  /* ═══════════════════════════════════
     FILTERING — always inside Furniture
  ═══════════════════════════════════ */
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list: any[] = (filteredListings || []).filter((l: any) => {
      if (!l.isPublished) return false;
      if (isOutOfStock(l)) return false;
      const chain = chainIdsOf(l.categoryId);
      if (!chain.includes(FURNITURE_DEPT_ID)) return false;
      if (!chain.includes(activeCat)) return false;
      if (activeSub && !chain.includes(activeSub)) return false;
      if (activeDeep && !chain.includes(activeDeep)) return false;
      if (q) {
        const detailText = detailsOf(l).map((d) => `${d.name} ${d.value}`).join(" ");
        const hay = `${l.name ?? ""} ${l.description ?? ""} ${l.category ?? ""} ${l.vendor ?? ""} ${(l.tags ?? []).join(" ")} ${detailText}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    if (activeSort === "price_asc") list = [...list].sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0));
    else if (activeSort === "price_desc") list = [...list].sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0));
    else if (activeSort === "name_asc") list = [...list].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    else if (activeSort === "newest") list = [...list].sort((a, b) => (Date.parse(b.createdAt ?? 0) || 0) - (Date.parse(a.createdAt ?? 0) || 0));

    return list;
  }, [filteredListings, activeCat, activeSub, activeDeep, searchQuery, activeSort]);

  const productsByBrand = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    filteredProducts.forEach((item: any) => {
      const bId = item.brandId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    let groups = order.map((bId) => ({ brandId: bId, items: map.get(bId)! }));
    if (locationActive) {
      groups = groups.filter((g) => {
        const tier = brandTier.get(g.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    return [...groups].sort((a, b) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3));
  }, [filteredProducts, showGlobal, brandTier, locationActive]);

  const visibleProductCount = useMemo(
    () => productsByBrand.reduce((sum, g) => sum + g.items.length, 0),
    [productsByBrand]
  );

  const activeFilterName = activeDeep ? catName(activeDeep) : activeSub ? catName(activeSub) : null;
  const catChildren = useMemo(() => childrenOf(activeCat), [activeCat]);

  /* ═══════════════════════════════════
     HEADER — plain function so the search keeps focus
  ═══════════════════════════════════ */
  const headerBar = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: isMobile ? "12px 14px" : "14px 20px" }}>
      {locationDenied ? (
        <button className="fn-hbtn" onClick={() => setLocationModalOpen(true)} aria-label="Location is off">
          <AlertTriangle size={14} />
        </button>
      ) : !userCountry ? (
        <div className="fn-hbtn">
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff", animation: "fnPulse 1.2s infinite" }} />
          <span style={{ display: isMobile ? "none" : "inline" }}>Locating…</span>
        </div>
      ) : (
        <button onClick={handleToggleGlobal} className="fn-hbtn">
          {showGlobal ? <Globe size={13} /> : <MapPin size={13} />}
          <span style={{ display: isMobile ? "none" : "inline" }}>{showGlobal ? "Global" : userCity || userCountry || "Nearby"}</span>
        </button>
      )}

      <div style={S.searchWrap}>
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search sofas, beds, dining tables…"
          aria-label="Search furniture"
          style={S.searchInput}
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} aria-label="Clear search"
            style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px", color: "#fff" }}>
            <X size={13} />
          </button>
        )}
        <button style={S.searchBtn} aria-label="Search"><Search size={16} color="#fff" /></button>
      </div>

      <button onClick={() => (isMobile ? setCatOpen(true) : setDesktopCatOpen((v) => !v))} className="fn-hbtn">
        <LayoutGrid size={14} />
        <span style={{ display: isMobile ? "none" : "inline" }}>Rooms</span>
      </button>

      <button onClick={goToCheckout} className="fn-hbtn" style={{ position: "relative" }}>
        <ShoppingCart size={16} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal} items` : "Cart"}</span>}
        {cartTotal > 0 && (
          <span style={{ position: "absolute", top: -6, right: -6, background: HEART.redDark, color: "#fff", fontSize: 9, fontWeight: 900, minWidth: 17, height: 17, padding: "0 4px", borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {cartTotal > 99 ? "99+" : cartTotal}
          </span>
        )}
      </button>

      {!isMobile && (
        <div style={{ position: "relative" }}>
          <button onClick={() => setProfileMenuOpen((v) => !v)} className="fn-hbtn" aria-label="Your account"><User size={15} /></button>
          <AnimatePresence>
            {profileMenuOpen && (
              <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", width: 200, background: "#fff", borderRadius: 16, border: T.outline, boxShadow: "0 16px 40px rgba(27,14,6,.2)", overflow: "hidden", zIndex: 400 }}>
                <button className="fn-nav-item" onClick={() => navigate(`/profile/${userId}`)}><User size={15} /> Profile</button>
                <button className="fn-nav-item" onClick={() => navigate(`/clientdashboard/${userId}`)}><ShoppingBag size={15} /> Orders</button>
                <button className="fn-nav-item" style={{ color: T.red, borderBottom: "none" }} onClick={handleLogout}><LogOut size={15} /> Sign out</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );

  /* ═══════════════════════════════════
     TITLE BANNER
  ═══════════════════════════════════ */
  const titleBanner = () => (
    <div style={{ padding: isMobile ? "6px 4px 4px" : "4px 4px 8px", display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
      <span className="fn-title" style={{ fontSize: isMobile ? 30 : 46 }}>
        {activeFilterName ?? cat?.name ?? "Furniture"}
      </span>
      <span style={{ fontSize: isMobile ? 11 : 13, fontWeight: 800, letterSpacing: "0.16em", color: T.mutedDim, textTransform: "uppercase" }}>
        {locationDenied ? "Worldwide" : showGlobal ? "Global" : userCity ?? "Near You"}
      </span>
    </div>
  );

  /* ═══════════════════════════════════
     THE OVAL — the rooms
  ═══════════════════════════════════ */
  const ovalToggleBar = () => (
    <div style={{ padding: isMobile ? "0 4px 14px" : "0 4px 18px" }}>
      <div className="fn-oval">
        <span style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0, padding: "0 6px 0 8px", color: HEART.goldDark }}>
          <Sofa size={14} />
        </span>
        {FURNITURE_CATEGORIES.map((c) => {
          const isActive = c.id === activeCat;
          return (
            <button key={c.id} className={`fn-oval-toggle${isActive ? " active" : ""}`} onClick={() => handleCatSelect(c.id)}>
              <span className="fn-oval-ic" style={{ color: isActive ? "#fff" : HEART.goldDark }}>{c.icon}</span>
              {c.name}
            </button>
          );
        })}
      </div>
    </div>
  );

  /* ═══════════════════════════════════
     SUBCATEGORY ICON BAR
  ═══════════════════════════════════ */
  const subIconBar = () => {
    if (!catChildren.length) return null;
    return (
      <div style={{ marginBottom: 22, padding: isMobile ? "14px 16px" : "18px 24px", background: HEART.panelBg, borderRadius: 18, border: T.outline }}>
        <div style={{ display: "flex", gap: isMobile ? 14 : 22, overflowX: "auto", scrollbarWidth: "none" as any, WebkitOverflowScrolling: "touch" as any, paddingBottom: 2 }}>
          {catChildren.map((node) => {
            const isSel = activeSub === node.id;
            return (
              <button key={node.id} onClick={() => handleSubSelect(node.id)}
                style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "2px 4px" }}>
                <div style={{
                  width: isMobile ? 46 : 54, height: isMobile ? 46 : 54, borderRadius: "50%", overflow: "hidden",
                  border: `2px solid ${isSel ? T.red : HEART.border}`,
                  boxShadow: isSel ? `0 0 0 3px ${T.redDim}` : "none", transition: "all .2s",
                }}>
                  {node.image ? (
                    <img src={node.image} alt={node.name} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: isSel ? 1 : 0.85 }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", background: isSel ? T.grad : HEART.goldSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ color: isSel ? "#fff" : HEART.goldDark, display: "flex", fontSize: isMobile ? 18 : 22 }}>{node.icon}</span>
                    </div>
                  )}
                </div>
                <span style={{ fontSize: 10, fontWeight: isSel ? 800 : 600, color: isSel ? T.red : T.black, whiteSpace: "nowrap" }}>{node.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     SHOP BY CATEGORY — 4-up tile cards, select never navigates
  ═══════════════════════════════════ */
  const shopByCategory = () => {
    if (!catChildren.length) return null;

    const focusId = activeSub ?? catChildren[0]?.id ?? activeCat;
    const tileSource = childrenOf(focusId);
    if (!tileSource.length) return null;

    const chunk4 = (arr: CategoryItem[]) => {
      const out: CategoryItem[][] = [];
      for (let i = 0; i < arr.length; i += 4) out.push(arr.slice(i, i + 4));
      return out;
    };
    const focusName = catName(focusId) || cat?.name;
    const chunks = chunk4(tileSource);
    const cardGroups = chunks.map((tiles, idx) => ({
      title: chunks.length > 1 ? `${focusName} ${idx + 1}` : (focusName || "All"),
      tiles,
    }));

    return (
      <div style={{ marginBottom: 28 }}>
        <div onClick={() => setShopCatExpanded((v) => !v)}
          style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none", marginBottom: 14 }}>
          <span style={{ fontWeight: 900, fontSize: isMobile ? 16 : 18, color: T.black, letterSpacing: -0.3 }}>Shop by Category</span>
          <motion.div animate={{ rotate: shopCatExpanded ? 0 : -90 }} transition={{ duration: 0.2 }}>
            <ChevronDown size={20} color={T.muted} />
          </motion.div>
        </div>

        <AnimatePresence initial={false}>
          {shopCatExpanded && (
            <motion.div key={activeCat} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
              <div style={isMobile
                ? { display: "flex", gap: 14, overflowX: "auto", scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" as any, scrollbarWidth: "none" as any, paddingBottom: 4 }
                : { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 18 }
              }>
                {cardGroups.map((group, gi) => (
                  <div key={`${group.title}-${gi}`}
                    style={{ flexShrink: 0, ...(isMobile ? { width: "78vw", scrollSnapAlign: "start" } : {}), background: "#fff", borderRadius: 20, overflow: "hidden", border: T.outline, boxShadow: "0 6px 28px rgba(27,14,6,.06)", transition: "box-shadow .22s, border-color .22s, transform .22s" }}
                    onMouseEnter={(e) => { const el = e.currentTarget as HTMLElement; el.style.boxShadow = `0 8px 0 ${T.redDim}, 0 16px 38px rgba(27,14,6,.16)`; el.style.borderColor = T.red; el.style.transform = "translateY(-4px)"; }}
                    onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.boxShadow = "0 6px 28px rgba(27,14,6,.06)"; el.style.borderColor = HEART.border; el.style.transform = "translateY(0)"; }}
                  >
                    <div style={{ padding: isMobile ? "10px 12px 6px" : "14px 16px 8px", fontWeight: 900, fontSize: isMobile ? 12 : 14, color: T.black, letterSpacing: -0.2 }}>{group.title}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? 4 : 8, padding: isMobile ? "0 8px 8px" : "0 10px 10px" }}>
                      {group.tiles.map((node: CategoryItem) => {
                        const sel = activeDeep === node.id;
                        const hasKids = childrenOf(node.id).length > 0;
                        return (
                          <button key={node.id} className="fn-scg-tile" onClick={() => handleDeepSelect(node.id)}
                            style={{ padding: 0, display: "flex", flexDirection: "column" }}>
                            <div style={{ width: "100%", aspectRatio: "4/3", borderRadius: 12, overflow: "hidden", position: "relative", background: `linear-gradient(180deg, ${FLOOR.wall} 0%, ${FLOOR.wallEdge} 68%, ${FLOOR.boardLight} 68%, ${FLOOR.boardDark} 100%)`, outline: sel ? `2.5px solid ${T.red}` : "none", outlineOffset: 2, transition: "outline .15s" }}>
                              {node.image ? (
                                <>
                                  <img src={node.image} alt={node.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top, ${HEART.dark}EE, transparent 55%)` }} />
                                  <span style={{ position: "absolute", bottom: isMobile ? 5 : 7, left: 4, right: 4, textAlign: "center", fontSize: isMobile ? 9 : 11, fontWeight: 800, color: "#fff", lineHeight: 1.15, textShadow: "0 1px 4px rgba(0,0,0,.5)" }}>{node.name}</span>
                                </>
                              ) : (
                                <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, padding: 4 }}>
                                  <span style={{ fontSize: isMobile ? 22 : 26, color: T.red, display: "flex" }}>{node.icon}</span>
                                  <span style={{ fontSize: isMobile ? 9 : 10.5, fontWeight: 800, color: T.black, textAlign: "center", lineHeight: 1.15 }}>{node.name}</span>
                                </div>
                              )}
                              {sel ? (
                                <div style={{ position: "absolute", top: 5, right: 5, width: 18, height: 18, borderRadius: "50%", background: T.red, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <Check size={11} color="#fff" strokeWidth={3} />
                                </div>
                              ) : hasKids ? (
                                <div style={{ position: "absolute", top: 5, right: 5, width: 16, height: 16, borderRadius: "50%", background: "rgba(255,255,255,.9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <ChevronRight size={10} color={T.red} />
                                </div>
                              ) : null}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  /* ═══════════════════════════════════
     SIDEBAR TREE
  ═══════════════════════════════════ */
  const TreeNode: React.FC<{ node: CategoryItem; depth: number }> = ({ node, depth }) => {
    const kids = childrenOf(node.id);
    const isOpen = openNodes.has(node.id);
    const isPicked = activeSub === node.id || activeDeep === node.id;

    return (
      <div style={depth === 0 ? A.category : { paddingLeft: 14, paddingTop: 7 }}>
        <div
          style={{ ...A.row, fontSize: depth === 0 ? 14 : 13, color: isPicked ? T.red : T.black }}
          onClick={() => {
            if (depth === 0) handleSubSelect(node.id);
            else handleDeepSelect(node.id);
            if (kids.length) toggleNode(node.id);
            else closePanel();
          }}
        >
          <span style={{ display: "flex", color: isPicked ? T.red : HEART.goldDark }}>{node.icon}</span>
          {node.name}
          {kids.length > 0 && (
            <motion.div animate={{ rotate: isOpen ? 90 : 0 }} style={{ marginLeft: "auto" }}>
              <ChevronRight size={13} color={T.mutedDim} />
            </motion.div>
          )}
          {isPicked && kids.length === 0 && <Check size={13} color={T.red} style={{ marginLeft: "auto" }} />}
        </div>
        <AnimatePresence>
          {isOpen && kids.length > 0 && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
              <div style={A.sub}>
                {kids.map((k) => <TreeNode key={k.id} node={k} depth={depth + 1} />)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const categoryTree = () => {
    const isOpen = openCatPanel === activeCat;
    return (
      <div style={A.commerceCard}>
        <div style={A.commerceHeader} onClick={() => setOpenCatPanel(isOpen ? null : activeCat)}>
          {cat?.name}
          <motion.div animate={{ rotate: isOpen ? 90 : 0 }}><ChevronRight size={16} /></motion.div>
        </div>
        <AnimatePresence>
          {isOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
              {catChildren.length === 0
                ? <p style={{ fontSize: 12, color: T.mutedDim, paddingTop: 10 }}>No sub-categories here yet.</p>
                : catChildren.map((c) => <TreeNode key={c.id} node={c} depth={0} />)}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const mobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.55)", zIndex: 400 }} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", borderRadius: "22px 22px 0 0", background: "#fff", zIndex: 401, border: T.outline, overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: HEART.border, borderRadius: 2, margin: "14px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${HEART.border}`, padding: "12px 20px 10px" }}>
              <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: "0.15em", color: T.red }}>ROOMS</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer" }}>
                <X size={15} color={T.muted} />
              </button>
            </div>
            <div style={{ padding: "16px 16px 32px" }}>{ovalToggleBar()}{categoryTree()}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     BRAND ROW — with the delivery distance, which for a
     wardrobe matters as much as the price
  ═══════════════════════════════════ */
  const brandRowHeader = (brandId: string) => {
    const partner = brandMap.get(brandId);
    const shopName = partner?.name || "Showroom";
    const shopLogo = partner?.image || appLogo;
    const accent = brandAccentColor(partner);
    const tier = brandTier.get(brandId);
    const tierLabel = locationActive && tier !== undefined ? TIER_LABEL[tier] : null;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "26px 0 12px" }}>
        <button onClick={() => goToBrand(brandId)} aria-label={`Open ${shopName}`}
          style={{ width: 34, height: 34, borderRadius: "50%", overflow: "hidden", flexShrink: 0, border: `1px solid ${T.border}`, background: "#fff", padding: 0, cursor: "pointer" }}>
          <img src={shopLogo} alt={shopName} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
        </button>
        <span onClick={() => goToBrand(brandId)}
          style={{ fontWeight: 900, fontSize: 14, color: T.black, textDecoration: "underline", textDecorationColor: accent, textDecorationThickness: 2, textUnderlineOffset: 4, cursor: "pointer" }}>
          {shopName}
        </span>
        {tierLabel && (
          <span className="fn-chip" style={{ flexShrink: 0 }}>
            <MapPin size={9} />{tierLabel}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <motion.button whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }} onClick={() => goToBrand(brandId)} aria-label={`Open ${shopName}`}
          style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: "#fff", boxShadow: "0 3px 10px rgba(27,14,6,.18)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronRight size={16} color={accent} />
        </motion.button>
      </div>
    );
  };

  /* ═══════════════════════════════════
     PRODUCT CARD — PSH logic, plinth photo
  ═══════════════════════════════════ */
  const productCard = (item: any) => {
    const accent = brandAccentColor(brandMap.get(item.brandId));
    const q = getQty(item.listingId);
    const summary = ratingSummaries[item.listingId];
    const discount = item.compareAtPrice && item.compareAtPrice > (item.rate ?? 0)
      ? Math.round(((item.compareAtPrice - (item.rate ?? 0)) / item.compareAtPrice) * 100) : null;
    const size = sizeDetailOf(item);
    const material = materialDetailOf(item);
    const isWished = wished.has(item.listingId);
    const low = (item.stock ?? 0) > 0 && (item.stock ?? 0) <= 3;

    return (
      <div key={item.listingId} className="fn-card" onClick={() => openProductModal(item)}>
        <div className={`fn-plinth${PHOTO_BLEND ? " blend" : ""}`}>
          <div className="fn-shadow" />
          {item.photos?.[0]
            ? <img src={item.photos[0]} alt={item.name}
                onError={(e) => { const el = e.currentTarget as HTMLImageElement; el.src = appLogo; el.style.opacity = "0.4"; }} />
            : <Package size={32} color={T.red} style={{ opacity: .4, position: "relative", zIndex: 1 }} />}

          {discount && (
            <span style={{ position: "absolute", zIndex: 2, top: 10, left: 10, background: T.red, color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 999 }}>
              {discount}% OFF
            </span>
          )}
          {low && (
            <span style={{ position: "absolute", zIndex: 2, top: discount ? 36 : 10, left: 10, background: "#fff", color: HEART.goldDark, fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 999, border: `1px solid ${HEART.border}` }}>
              only {item.stock} left
            </span>
          )}
          <button onClick={(e) => toggleWish(item.listingId, e)} aria-label={isWished ? "Remove from saved" : "Save for later"}
            style={{ position: "absolute", zIndex: 2, top: 10, right: 10, width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,.92)", border: `1px solid ${HEART.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <Heart size={13} color={isWished ? accent : T.muted} fill={isWished ? accent : "none"} />
          </button>
        </div>

        <div style={{ padding: "12px 4px 0", display: "flex", flexDirection: "column", flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: T.black, lineHeight: 1.3, marginBottom: 3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", minHeight: 34 }}>
            {item.name}
          </div>
          <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 8 }}>
            {catPathLabel(item.categoryId) || item.category || "piece"}
            {summary?.totalReviews ? ` · ${summary.avgRating.toFixed(1)}★ (${summary.totalReviews})` : ""}
          </div>

          {(size || material) && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
              {size && <span className="fn-chip size"><Ruler size={10} />{size.value}</span>}
              {material && <span className="fn-chip"><Layers size={10} />{material.value}</span>}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: "auto" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, minWidth: 0 }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: T.black }}>{money(item.rate ?? 0)}</span>
              {discount && (
                <span style={{ fontSize: 11, color: T.mutedDim, textDecoration: "line-through" }}>{money(item.compareAtPrice ?? 0)}</span>
              )}
            </div>
            {q > 0 && !hasVariants(item) ? (
              <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 6, background: HEART.goldSoft, borderRadius: 999, padding: "3px 6px" }}>
                <button className="fn-plus" aria-label="Remove one"
                  onClick={(e) => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}
                  style={{ width: 22, height: 22, borderRadius: "50%", border: "none", background: accent, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Minus size={12} />
                </button>
                <span style={{ fontSize: 12, fontWeight: 800, color: T.black, minWidth: 12, textAlign: "center" }}>{q}</span>
                <button className="fn-plus" aria-label="Add one" onClick={(e) => handleAddToCart(item, e)}
                  style={{ width: 22, height: 22, borderRadius: "50%", border: "none", background: accent, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Plus size={13} />
                </button>
              </div>
            ) : (
              <button className="fn-plus" onClick={(e) => handleAddToCart(item, e)}
                aria-label={hasVariants(item) ? "Choose options" : "Add to bag"}
                style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: accent, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, boxShadow: `0 3px 10px ${accent}59` }}>
                <Plus size={16} color="#fff" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     EMPTY STATES
  ═══════════════════════════════════ */
  const emptyState = (title: string, body: string, cta: { label: string; onClick: () => void }) => (
    <div style={{ textAlign: "center", padding: 56 }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 14, color: HEART.goldDark, animation: "fnFloat 3.6s ease-in-out infinite" }}>
        <Armchair size={44} />
      </div>
      <h3 style={{ fontWeight: 900, fontSize: 22, color: T.black, marginBottom: 10 }}>{title}</h3>
      <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>{body}</p>
      <button onClick={cta.onClick}
        style={{ padding: "12px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>
        {cta.label}
      </button>
    </div>
  );

  const renderProducts = (isMob: boolean) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 4, height: 22, borderRadius: 2, background: GRAD_H }} />
          <span style={{ fontWeight: 900, fontSize: 15, color: T.black }}>
            {activeFilterName ?? (locationDenied ? "All Products" : showGlobal ? "Global" : userCity ? `Near ${userCity}` : userCountry ?? "All Products")}
          </span>
          <span style={{ fontSize: 12, color: T.muted, background: HEART.goldSoft, padding: "2px 9px", borderRadius: 999 }}>{visibleProductCount}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <select value={activeSort} onChange={(e) => setActiveSort(e.target.value as SortKey)} aria-label="Sort products"
            style={{ border: T.outline, borderRadius: 999, padding: "5px 12px", fontSize: 11, fontWeight: 700, color: T.muted, background: "#fff", cursor: "pointer", outline: "none" }}>
            <option value="default">Featured</option>
            <option value="newest">Newest</option>
            <option value="price_asc">Price: low → high</option>
            <option value="price_desc">Price: high → low</option>
            <option value="name_asc">Name A–Z</option>
          </select>
          {locationActive && (
            <button onClick={handleToggleGlobal}
              style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? HEART.bg : T.redDim, borderRadius: 999, padding: "5px 13px", border: `1.5px solid ${showGlobal ? HEART.border : "rgba(200,32,46,.3)"}`, fontSize: 11, fontWeight: 700, color: showGlobal ? T.mutedDim : T.red, cursor: "pointer" }}>
              {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}{showGlobal ? "Show Local" : "Show Global"}
            </button>
          )}
        </div>
      </div>

      {productsByBrand.length === 0 ? (
        filteredProducts.length === 0 ? (
          emptyState(
            activeFilterName ? `No ${activeFilterName} in stock` : `No ${cat?.name ?? "Furniture"} pieces yet`,
            "Pick another room on the bar above, or clear your filters.",
            { label: "View All Rooms", onClick: resetAllFilters }
          )
        ) : locationDenied ? (
          emptyState(
            "Location needed",
            "Furniture ships from a real showroom. Turn on location to see the ones that deliver to you.",
            { label: "Enable Location", onClick: () => setLocationModalOpen(true) }
          )
        ) : (
          emptyState(
            showGlobal ? `No global ${cat?.name ?? "Furniture"} showrooms found.` : `No local ${cat?.name ?? "Furniture"} showrooms found.`,
            `Try switching to ${showGlobal ? "Local" : "Global"} above, or clear your filters.`,
            { label: "View All Products", onClick: resetAllFilters }
          )
        )
      ) : (
        productsByBrand.map((group) => (
          <div key={group.brandId}>
            {brandRowHeader(group.brandId)}
            <div style={isMob ? S.mobileGrid : S.grid}>
              {group.items.map((item: any) => productCard(item))}
            </div>
          </div>
        ))
      )}
    </div>
  );

  /* ═══════════════════════════════════
     DETAIL — PSH modal, plus the spec sheet
  ═══════════════════════════════════ */
  const detailModal = () => {
    if (!detailOpen || !detailItem) return null;
    const item = detailItem;
    const partner = brandMap.get(item.brandId);
    const accent = brandAccentColor(partner);
    const brandName = partner?.name;
    const previewImg = item.photos?.[0];
    const stock = item.stock ?? 0;
    const outOfStock = isOutOfStock(item);
    const discount = item.compareAtPrice && item.compareAtPrice > (item.rate ?? 0)
      ? Math.round(((item.compareAtPrice - (item.rate ?? 0)) / item.compareAtPrice) * 100) : null;

    const variantDefs = variantAttrsForItem(item);
    const specs = detailsOf(item);
    const summary = ratingSummaries[item.listingId];

    const allVariantsChosen = variantDefs.every((a: any) => selectedVariants[String(a.id)] != null);
    const canContinue = !outOfStock && qty > 0 && qty <= stock && allVariantsChosen;

    const closeBtn = (
      <button onClick={closeProductModal} aria-label="Close"
        style={{ background: "rgba(255,255,255,.92)", border: "none", width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(27,14,6,.18)" }}>
        <X size={15} color={T.black} />
      </button>
    );

    const content = (
      <div style={{ background: "#fff", borderRadius: isMobile ? "24px 24px 0 0" : 20, overflow: "hidden", maxWidth: isMobile ? "100%" : 460, width: "100%", maxHeight: isMobile ? "85vh" : "80vh", display: "flex", flexDirection: "column" }}>
        <div style={{ height: 4, background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 4s ease infinite", flexShrink: 0 }} />
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: HEART.border, margin: "12px auto 0", flexShrink: 0 }} />}

        <div style={{ overflowY: "auto", padding: "20px 20px 0" }}>
          {previewImg ? (
            <div className={`fn-plinth${PHOTO_BLEND ? " blend" : ""}`} style={{ marginBottom: 16, borderRadius: 16 }}>
              <div className="fn-shadow" />
              <img src={previewImg} alt="" />
              {discount && (
                <div style={{ position: "absolute", zIndex: 2, top: 12, left: 12, background: T.red, color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 999 }}>{discount}% OFF</div>
              )}
              <div style={{ position: "absolute", zIndex: 2, top: 12, right: 12 }}>{closeBtn}</div>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>{closeBtn}</div>
          )}

          {brandName && (
            <p style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".1em", color: accent, textTransform: "uppercase", marginBottom: 4 }}>{brandName}</p>
          )}
          <div style={{ fontSize: 15, fontWeight: 800, color: T.black, marginBottom: 4 }}>{item.name}</div>

          {item.categoryId && (
            <p style={{ fontSize: 10.5, fontWeight: 700, color: T.mutedDim, marginBottom: 8, display: "flex", alignItems: "center", gap: 5 }}>
              <Tag size={10} /> {catPathLabel(item.categoryId)}
            </p>
          )}

          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
            <span style={{ fontSize: 20, fontWeight: 900, color: T.black }}>{money(item.rate ?? 0)}</span>
            {discount && <span style={{ fontSize: 13, color: T.mutedDim, textDecoration: "line-through" }}>{money(item.compareAtPrice ?? 0)}</span>}
            {summary?.totalReviews ? (
              <span style={{ fontSize: 11.5, fontWeight: 700, color: T.muted, display: "flex", alignItems: "center", gap: 4 }}>
                <Star size={11} fill={HEART.gold} color={HEART.gold} />
                {summary.avgRating.toFixed(1)} ({summary.totalReviews.toLocaleString()})
              </span>
            ) : null}
          </div>

          {/* ── selectable variants (Finish, Fabric, …) ── */}
          {variantDefs.map((attr: any) => {
            const key = String(attr.id);
            const chosen = selectedVariants[key];
            const isColour = attr.inputType === "color";
            return (
              <div key={key} style={{ marginBottom: 18 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: T.mutedDim, textTransform: "uppercase" }}>{attr.name}</span>
                  {chosen == null && <span style={{ fontSize: 10, fontWeight: 700, color: T.red }}>Please select</span>}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {attr.options.map((opt: any) => {
                    const sel = String(chosen) === String(opt.id);
                    return (
                      <button key={opt.id} onClick={() => setSelectedVariants((prev) => ({ ...prev, [key]: opt.id }))}
                        style={{
                          display: "flex", alignItems: "center", gap: 7,
                          padding: isColour ? "6px 14px 6px 7px" : "8px 14px",
                          borderRadius: 999, cursor: "pointer", fontSize: 12, fontWeight: 700,
                          border: `1.5px solid ${sel ? T.red : T.border}`,
                          background: sel ? T.redDim : "#fff",
                          color: sel ? T.red : T.black, transition: "all .15s",
                        }}>
                        {isColour && (
                          <span style={{ width: 18, height: 18, borderRadius: "50%", background: opt.hex || HEART.goldSoft, border: "1px solid rgba(27,14,6,.15)" }} />
                        )}
                        {opt.name || opt.standard || String(opt.id)}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* ── the spec sheet: will it fit, what's it made of ── */}
          {specs.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: T.mutedDim, textTransform: "uppercase", marginBottom: 9 }}>
                Measurements &amp; materials
              </div>
              <dl className="fn-spec">
                {specs.map((c) => (
                  <React.Fragment key={c.name}>
                    <dt>{c.name}</dt>
                    <dd>{c.value}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          )}

          {item.vendor && (
            <p style={{ fontSize: 11.5, color: T.muted, marginBottom: 8 }}>
              <strong style={{ color: T.black }}>Vendor:</strong> {item.vendor}
            </p>
          )}
          {item.tags?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
              {item.tags.map((t: string) => <span key={t} className="fn-chip">#{t}</span>)}
            </div>
          )}

          {item.description && (
            <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.65, marginBottom: 18 }}
              dangerouslySetInnerHTML={{ __html: item.description }} />
          )}

          {outOfStock ? (
            <p style={{ fontSize: 11, fontWeight: 700, color: T.mutedDim, marginBottom: 10 }}>Out of stock</p>
          ) : stock <= 3 ? (
            <p style={{ fontSize: 11, fontWeight: 700, color: T.red, marginBottom: 10 }}>Only {stock} left</p>
          ) : null}

          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.16em", color: T.mutedDim }}>QTY</span>
            <div style={{ display: "flex", border: `1.5px solid ${T.border}`, borderRadius: 12, overflow: "hidden", background: HEART.bg }}>
              <button onClick={() => setQty((v) => Math.max(1, v - 1))} aria-label="One fewer"
                style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: "pointer", fontSize: 16, color: T.red }}>−</button>
              <div style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: T.black, borderLeft: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}` }}>{qty}</div>
              <button onClick={() => setQty((v) => Math.min(stock || 1, v + 1))} disabled={qty >= stock} aria-label="One more"
                style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: qty >= stock ? "not-allowed" : "pointer", fontSize: 16, color: T.red, opacity: qty >= stock ? 0.4 : 1 }}>+</button>
            </div>
          </div>
        </div>

        <div style={{ padding: "14px 20px 20px", display: "flex", gap: 10, flexShrink: 0, borderTop: `1px solid ${T.border}` }}>
          <button disabled={!canContinue} onClick={() => handleDetailContinue(false)}
            style={{ flex: 1, padding: "13px 0", borderRadius: 12, border: `1.5px solid ${T.black}`, background: "transparent", color: T.black, fontWeight: 700, fontSize: 13, cursor: canContinue ? "pointer" : "not-allowed", opacity: canContinue ? 1 : 0.45 }}>
            Add to Bag
          </button>
          <button disabled={!canContinue} onClick={() => handleDetailContinue(true)}
            style={{ flex: 1, padding: "13px 0", borderRadius: 12, border: "none", background: canContinue ? GRAD_H : "#C8C8C8", color: "#fff", fontWeight: 800, fontSize: 13, cursor: canContinue ? "pointer" : "not-allowed" }}>
            Buy Now
          </button>
        </div>
      </div>
    );

    return (
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeProductModal}
          style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.7)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", zIndex: 500 }}>
          {isMobile ? (
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
              onClick={(e) => e.stopPropagation()} style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 501 }}>
              {content}
            </motion.div>
          ) : (
            <div style={{ position: "fixed", inset: 0, zIndex: 501, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
              <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }}
                transition={{ type: "spring", damping: 26, stiffness: 340 }} style={{ width: 460, borderRadius: 20, overflow: "hidden", boxShadow: "0 32px 80px rgba(27,14,6,.35)" }}>
                {content}
              </motion.div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    );
  };

  const locationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.8)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <motion.div initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }} onClick={(e) => e.stopPropagation()}
            style={{ width: 320, borderRadius: 20, padding: 24, background: "#fff", boxShadow: "0 30px 80px rgba(27,14,6,.5)", textAlign: "center" }}>
            <div style={{ height: 5, borderRadius: 999, background: GRAD_H, marginBottom: 20 }} />
            <div style={{ fontSize: 32, marginBottom: 10 }}>📍</div>
            <h3 style={{ fontWeight: 900, marginBottom: 8, color: T.black }}>Location Disabled</h3>
            <p style={{ color: T.muted, margin: "0 0 22px", fontSize: 13, lineHeight: 1.6 }}>
              Enable location in your browser settings to see showrooms that can deliver to you.
            </p>
            <button onClick={() => setLocationModalOpen(false)}
              style={{ padding: "11px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 12 }}>
              Got it
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     RENDER — no bottom tabs, AtlasHome owns navigation
  ═══════════════════════════════════ */
  return (
    <div className="fn-page" style={S.page}>
      <style>{GLOBAL_CSS}</style>
      <PageWatermark />

      <div style={S.content}>
        <header style={S.headerTop}>{headerBar()}</header>

        {mobileCategorySheet()}

        {!isMobile && (
          <div style={{ ...S.desktopGrid, gridTemplateColumns: desktopCatOpen ? "300px 1fr" : "1fr" }}>
            {desktopCatOpen && <aside style={A.sidebar}>{categoryTree()}</aside>}
            <main style={{ minWidth: 0 }}>
              {titleBanner()}
              {ovalToggleBar()}
              {subIconBar()}
              {shopByCategory()}
              {renderProducts(false)}
            </main>
          </div>
        )}

        {isMobile && (
          <div style={{ padding: "14px 12px 0" }}>
            {titleBanner()}
            {ovalToggleBar()}
            {subIconBar()}
            <div style={{ marginTop: 16 }}>{shopByCategory()}</div>
            {renderProducts(true)}
          </div>
        )}

        {detailModal()}
        {locationModal()}
      </div>
    </div>
  );
};

export default FurnitureStore;
